/**
 * What a locale *is*, for both runtimes.
 *
 * Two locales ship: `en` and `it` (docs/I18N.md). English is the source language; Italian is
 * the translation. A locale is stored as the bare language subtag — `User.locale` is
 * `VARCHAR(8)` and accepts `en-GB`, so everything that reads it normalises first.
 *
 * The *display* locale handed to `Intl` is a different string: `en` formats as `en-GB`
 * (`7 Aug 2026`, `€18.00`), never `en-US`. Never pass a `Locale` straight to `Intl` — pass
 * `intlLocaleOf(locale)`.
 */

export const LOCALES = ['en', 'it'] as const

export type Locale = (typeof LOCALES)[number]

/** The last resort, when nothing else said anything. */
export const DEFAULT_LOCALE: Locale = 'en'

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)

/**
 * `it-IT` → `it`, `EN_gb` → `en`, `es` → `null`.
 * Returns `null` rather than the default so callers can keep walking their fallback chain.
 */
export const normalizeLocale = (value: string | null | undefined): Locale | null => {
  if (typeof value !== 'string' || value.length === 0) return null
  const base = value.trim().toLowerCase().split(/[-_]/)[0] ?? ''
  return isLocale(base) ? base : null
}

/** The first candidate that resolves to a shipped locale. */
export const pickLocale = (
  candidates: readonly (string | null | undefined)[],
): Locale | null => {
  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate)
    if (locale !== null) return locale
  }
  return null
}

/** The BCP-47 tag every `Intl` constructor gets. See docs/I18N.md, "Italian typography". */
export const INTL_LOCALE: Record<Locale, string> = {
  en: 'en-GB',
  it: 'it-IT',
}

export const intlLocaleOf = (locale: Locale): string => INTL_LOCALE[locale]

/**
 * A language is labelled in its own language, always — `Italiano`, never `Italian`.
 * Identical in every catalogue, which is why it lives here and not in a translation file.
 */
export const LOCALE_NATIVE_NAME: Record<Locale, string> = {
  en: 'English',
  it: 'Italiano',
}

/** `hreflang` / `<html lang>` value for a locale. */
export const HTML_LANG: Record<Locale, string> = {
  en: 'en',
  it: 'it',
}

/** Everything priced in this product is priced in euro. */
export const DEFAULT_CURRENCY = 'EUR'

/**
 * `Accept-Language: it-IT,it;q=0.9,en;q=0.8` → `it`.
 *
 * Honoured for visitors who have never chosen, and for unauthenticated server-rendered flows:
 * password reset, invitations, the white-label sign-in page.
 */
export const parseAcceptLanguage = (header: string | null | undefined): Locale | null => {
  if (typeof header !== 'string' || header.trim().length === 0) return null

  const ranked = header
    .split(',')
    .map((part, index) => {
      const [tag = '', ...rest] = part.trim().split(';')
      const qParam = rest.find((entry) => entry.trim().startsWith('q='))
      const quality = qParam === undefined ? 1 : Number.parseFloat(qParam.trim().slice(2))
      return {
        tag: tag.trim(),
        quality: Number.isFinite(quality) ? quality : 0,
        // Preserves the header's own order between equal q-values.
        index,
      }
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    .sort((a, b) => (b.quality - a.quality !== 0 ? b.quality - a.quality : a.index - b.index))

  return pickLocale(ranked.map((entry) => entry.tag))
}
